import { useState } from 'react'
import type { ReactNode } from 'react'
import { Button, InputGroup } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { BadRequest } from 'shared/errors/bad-request'
import { login } from '../api/community-api'
import { useJoinCommunity } from '../api/use-join-community'
import { useAcceptInvitationFlow } from '../members/hooks/use-accept-invitation-flow'

interface UserLoginFormInterface {
  email: string
  password: string
}

const defaultErrors: UserLoginFormInterface = {
  email: '',
  password: '',
}

const defaultLoginData: UserLoginFormInterface = {
  email: '',
  password: '',
}

function LoginForm(): ReactNode {
  const { t } = useTranslation()
  const router = useRouter()
  const { confirmationToken, invitationToken } = router.query
  const communityPath = router.query.communityPath ?? ''
  const joinCommunityMutation = useJoinCommunity()
  const acceptInvitation = useAcceptInvitationFlow({ communityPath })
  const [errors, setErrors] = useState(defaultErrors)
  const [commonError, setCommonError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loginData, setLoginData] = useState(defaultLoginData)

  async function submit(): Promise<void> {
    if (!communityPath) return
    // TODO: remake to use TanStack Query
    try {
      setIsLoading(true)
      await login(loginData.email, loginData.password)

      if (invitationToken) {
        try {
          await acceptInvitation({
            invitationToken,
            confirmationToken: confirmationToken ?? undefined,
          })
        } catch {
          setIsLoading(false)
          setCommonError(t('core.error.internal_error_message'))
        }
        return
      }

      await joinCommunityMutation.mutateAsync(communityPath)
      globalThis.location.replace(`/community/${communityPath}`)
    } catch (error) {
      setIsLoading(false)
      if (error instanceof BadRequest) {
        if (error.errors.fields) {
          setErrors({
            email: error.errors.fields.email?.join('\n'),
            password: error.errors.fields.password?.join('\n'),
          })
        } else if (error.errors.common) {
          setCommonError(error.errors.common.join('\n'))
        }
      }
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-auto flex-col gap-1">
        <label className="text-sm font-medium text-text-primary">{t('global.email_address')}</label>
        <InputGroup.Root>
          <InputGroup.Input
            type="email"
            value={loginData.email}
            onChange={e => {
              setLoginData(prevState => ({ ...prevState, email: e.target.value }))
            }}
            maxLength={180}
          />
        </InputGroup.Root>
        {errors.email && <p className="text-sm text-text-error">{errors.email}</p>}
      </div>
      <div className="flex flex-auto flex-col gap-1">
        <label className="text-sm font-medium text-text-primary">{t('global.password')}</label>
        <InputGroup.Root>
          <InputGroup.Input
            type="password"
            value={loginData.password}
            onChange={e => {
              setLoginData(prevState => ({
                ...prevState,
                password: e.target.value,
              }))
            }}
            maxLength={180}
          />
        </InputGroup.Root>
        {errors.password && <p className="text-sm text-text-error">{errors.password}</p>}
      </div>
      <Button
        variant="primary"
        className="w-full disabled:border-button-disabled disabled:bg-button-disabled"
        isLoading={isLoading}
        onClick={submit}
        disabled={!loginData.email || !loginData.password || isLoading}
      >
        {t('join_form.button.login.title')}
      </Button>
      {commonError && <p className="mt-2 text-center text-sm text-text-error">{commonError}</p>}
    </div>
  )
}

export default LoginForm
